import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Bookmark, BookmarkCheck, FolderPlus, Folder, Plus, Check, Loader2, X, Tag,
} from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';
import axios from 'axios';

import { useTranslation } from 'react-i18next';

/**
 * BookmarkOrganizePopover — wraps the existing bookmark icon in a
 * popover that lets the user assign a Group and Labels to the bookmark
 * directly from the chat response, without going to /bookmarks.
 *
 * UX:
 *   • Not yet bookmarked → click saves instantly + popover opens
 *     showing "Saved · organize:" so the user can optionally pick a
 *     group/labels in the same gesture.
 *   • Already bookmarked → click opens the popover showing current
 *     group + labels with a "Remove bookmark" footer link.
 *
 * Existing endpoints (no backend change):
 *   GET    /api/bookmarks                       (resolve message_id → bookmark_id)
 *   POST   /api/bookmarks                       (initial save)
 *   DELETE /api/bookmarks/{bookmark_id}         (remove)
 *   GET    /api/bookmark-groups
 *   POST   /api/bookmark-groups
 *   PUT    /api/bookmarks/{bookmark_id}/group   (move; null group_id = ungroup)
 *   GET    /api/bookmark-labels
 *   POST   /api/bookmark-labels
 *   POST   /api/bookmarks/{bookmark_id}/labels  (additive merge)
 *   DELETE /api/bookmarks/{bookmark_id}/labels/{label_id}
 */

export default function BookmarkOrganizePopover({
  msg,
  isBookmarked,
  isGuest,
  onGuestBlock,
  onBookmarkAdded,    // (message_id, { bookmark_id }) — parent updates bookmarkedIds + meta
  onBookmarkRemoved,  // (message_id)
  onBookmarkMetaChange, // (message_id, partial) — parent updates meta map
  onGroupCreated,     // (group) — parent caches new group in groupsById
  onLabelCreated,     // (label) — parent caches new label in labelsById
  groupsById,         // { [group_id]: group }   (for cheap lookups)
  labelsById,         // { [label_id]: label }
  meta,               // current bookmark meta from parent (group_id, label_ids, ...)
  conversationId,
  testIdSuffix,
}) {
  const {
    t
  } = useTranslation();

  const [open, setOpen] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(meta?.bookmark_id || null);
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep local bookmarkId in sync with parent meta (handles cross-bubble
  // resolution where parent already has the bookmark_id loaded).
  useEffect(() => {
    if (meta?.bookmark_id && meta.bookmark_id !== bookmarkId) {
      setBookmarkId(meta.bookmark_id);
    }
    if (!isBookmarked && bookmarkId) {
      setBookmarkId(null);
    }
  }, [meta?.bookmark_id, isBookmarked, bookmarkId]);

  // Remembers the most recent action so we can show "Saved" one-shot
  // header when the popover opens right after an instant save.
  const justSavedRef = useRef(false);

  const handleClick = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (isGuest) {
      onGuestBlock?.('bookmarks');
      return;
    }
    if (isBookmarked) {
      // Already saved — open the manage popover.
      setOpen(true);
      return;
    }
    // Save instantly, then open the organizer for one-tap categorisation.
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bookmarks`, {
        type: 'message',
        content: (msg.content || '').substring(0, 500),
        conversation_id: conversationId,
        message_id: msg.message_id,
      }, { withCredentials: true });
      const bm = res.data?.bookmark || res.data;
      const newId = bm?.bookmark_id || null;
      if (newId) setBookmarkId(newId);
      onBookmarkAdded?.(msg.message_id, { bookmark_id: newId });
      justSavedRef.current = true;
      setOpen(true);
    } catch {
      toast.error(t('generated.bookmarkorganizepopover_could_not_save_febf'));
    } finally {
      setSaving(false);
    }
  }, [isBookmarked, isGuest, msg, conversationId, onBookmarkAdded, onGuestBlock]);

  // Resolve the bookmark_id for already-saved bookmarks the first time
  // the popover opens (the parent only tracks message_ids).
  useEffect(() => {
    if (!open || bookmarkId || !isBookmarked) return;
    let cancelled = false;
    setResolving(true);
    axios.get(`${API}/bookmarks`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const bm = (res.data?.bookmarks || []).find((b) => b.message_id === msg.message_id);
        if (bm?.bookmark_id) setBookmarkId(bm.bookmark_id);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [open, bookmarkId, isBookmarked, msg.message_id]);

  const handleRemove = useCallback(async () => {
    if (!bookmarkId) return;
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}`, { withCredentials: true });
      onBookmarkRemoved?.(msg.message_id);
      setBookmarkId(null);
      setOpen(false);
      toast.success(t('generated.bookmarkorganizepopover_bookmark_removed_a31e'));
    } catch {
      toast.error(t('generated.bookmarkorganizepopover_could_not_remove_a106'));
    }
  }, [bookmarkId, msg.message_id, onBookmarkRemoved]);

  const showJustSaved = justSavedRef.current && open && isBookmarked;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) justSavedRef.current = false;
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
          onClick={handleClick}
          data-testid={`bookmark-msg-${testIdSuffix}`}
          style={isGuest ? { opacity: 0.4 } : undefined}
          aria-label={isBookmarked ? 'Manage bookmark' : 'Save bookmark'}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          ) : !isGuest && isBookmarked ? (
            <BookmarkCheck className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
          ) : (
            <Bookmark className="h-4 w-4 theme-text-secondary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        avoidCollisions
        className="w-80 p-0 overflow-hidden"
        style={{
          background: 'var(--theme-surface)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 16px 48px -16px rgba(0,0,0,0.5)',
        }}
        data-testid={`bookmark-popover-${testIdSuffix}`}
        onClick={(e) => e.stopPropagation()}
      >
        <OrganizerPanel
          messageId={msg.message_id}
          bookmarkId={bookmarkId}
          resolving={resolving}
          showJustSaved={showJustSaved}
          parentMeta={meta}
          parentGroupsById={groupsById}
          parentLabelsById={labelsById}
          onMetaChange={onBookmarkMetaChange}
          onGroupCreated={onGroupCreated}
          onLabelCreated={onLabelCreated}
          onRemove={handleRemove}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

/* The actual organizer body — split out so the popover can lazy-mount
   it (we don't fetch groups/labels until the user actually opens). */
function OrganizerPanel({
  messageId,
  bookmarkId,
  resolving,
  showJustSaved,
  parentMeta,
  parentGroupsById,
  parentLabelsById,
  onMetaChange,
  onGroupCreated,
  onLabelCreated,
  onRemove,
  onClose,
}) {
  const {
    t
  } = useTranslation();

  // Seed local lists from the parent caches so the popover paints
  // instantly with whatever the chip row is already showing — no
  // initial spinner unless we genuinely haven't fetched yet.
  const seedGroups = useMemo(
    () => Object.values(parentGroupsById || {}),
    [parentGroupsById],
  );
  const seedLabels = useMemo(
    () => Object.values(parentLabelsById || {}),
    [parentLabelsById],
  );
  const [groups, setGroups] = useState(seedGroups);
  const [labels, setLabels] = useState(seedLabels);
  const [loading, setLoading] = useState(seedGroups.length === 0 && seedLabels.length === 0);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [busy, setBusy] = useState(null); // id of group/label being toggled

  const refresh = useCallback(async () => {
    if (!bookmarkId) return;
    setLoading(true);
    try {
      const [groupsRes, labelsRes] = await Promise.all([
        axios.get(`${API}/bookmark-groups`, { withCredentials: true }),
        axios.get(`${API}/bookmark-labels`, { withCredentials: true }),
      ]);
      setGroups(groupsRes.data?.groups || []);
      setLabels(labelsRes.data?.labels || []);
    } catch {
      // soft-fail — popover stays usable for its already-loaded state
    } finally {
      setLoading(false);
    }
  }, [bookmarkId]);

  // Refresh on mount only when we don't already have a seed payload
  // from the parent — keeps the popover snappy on every open after the
  // first load of the chat page.
  useEffect(() => {
    if (bookmarkId && groups.length === 0 && labels.length === 0) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarkId]);

  const currentGroupId = parentMeta?.group_id || null;
  const currentLabelIds = useMemo(
    () => new Set(parentMeta?.label_ids || []),
    [parentMeta?.label_ids],
  );

  const moveToGroup = async (groupId) => {
    if (!bookmarkId) return;
    setBusy(`group:${groupId || 'none'}`);
    try {
      await axios.put(
        `${API}/bookmarks/${bookmarkId}/group`,
        { group_id: groupId },
        { withCredentials: true },
      );
      onMetaChange?.(messageId, { group_id: groupId || null });
    } catch {
      toast.error(t('generated.bookmarkorganizepopover_could_not_update_55c3'));
    } finally {
      setBusy(null);
    }
  };

  const toggleLabel = async (labelId) => {
    if (!bookmarkId) return;
    const isOn = currentLabelIds.has(labelId);
    setBusy(`label:${labelId}`);
    try {
      if (isOn) {
        await axios.delete(
          `${API}/bookmarks/${bookmarkId}/labels/${labelId}`,
          { withCredentials: true },
        );
        onMetaChange?.(messageId, {
          label_ids: (parentMeta?.label_ids || []).filter((id) => id !== labelId),
        });
      } else {
        if (currentLabelIds.size >= 5) {
          toast.error(t('generated.bookmarkorganizepopover_up_to_5_6d53'));
          return;
        }
        const res = await axios.post(
          `${API}/bookmarks/${bookmarkId}/labels`,
          { label_ids: [labelId] },
          { withCredentials: true },
        );
        const merged = res.data?.label_ids || [...(parentMeta?.label_ids || []), labelId];
        onMetaChange?.(messageId, { label_ids: merged });
      }
    } catch {
      toast.error(t('generated.bookmarkorganizepopover_could_not_update_b928'));
    } finally {
      setBusy(null);
    }
  };

  const handleCreateGroup = async (e) => {
    e?.preventDefault?.();
    const name = newGroupName.trim();
    if (!name) return;
    setCreatingGroup(true);
    try {
      const res = await axios.post(
        `${API}/bookmark-groups`,
        { name },
        { withCredentials: true },
      );
      const created = res.data;
      const next = { ...created, bookmark_count: 0 };
      setGroups((prev) => [...prev, next]);
      onGroupCreated?.(next);
      setNewGroupName('');
      // Auto-assign the newly-created group to this bookmark.
      if (created?.group_id) {
        await moveToGroup(created.group_id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleCreateLabel = async (e) => {
    e?.preventDefault?.();
    const name = newLabelName.trim();
    if (!name) return;
    setCreatingLabel(true);
    try {
      // Pick a deterministic colour based on existing labels so the
      // palette spreads instead of always grabbing red.
      const palette = ['#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#eab308', '#ec4899', '#14b8a6', '#6366f1', '#f43f5e'];
      const color = palette[labels.length % palette.length];
      const res = await axios.post(
        `${API}/bookmark-labels`,
        { name, color },
        { withCredentials: true },
      );
      const created = res.data;
      setLabels((prev) => [...prev, created]);
      onLabelCreated?.(created);
      setNewLabelName('');
      // Auto-toggle the newly-created label on for this bookmark.
      if (created?.label_id) {
        await toggleLabel(created.label_id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create label');
    } finally {
      setCreatingLabel(false);
    }
  };

  if (resolving || (!bookmarkId && loading)) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      style={{
        // Viewport-aware cap: hard-limit to 420px on desktop, but on
        // tablets/mobile clamp to 70% of the small viewport height so
        // the popover never runs past the fold. iPad Safari + Radix
        // fixed positioning was silently clipping items below the
        // viewport when the raw 420px cap exceeded available space.
        maxHeight: 'min(420px, 70svh)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b shrink-0"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {showJustSaved ? (
            <>
              <Check className="h-4 w-4" style={{ color: '#22c55e' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('generated.bookmarkorganizepopover_saved_organize_a9fa')}
              </p>
            </>
          ) : (
            <>
              <BookmarkCheck className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('generated.bookmarkorganizepopover_bookmark_c0b6')}
              </p>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full hover:bg-[var(--theme-hover)] transition"
          aria-label={t('generated.bookmarkorganizepopover_close_5907')}
          data-testid="bookmark-popover-close"
        >
          <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
        </button>
      </div>
      {/* Scrollable body — `min-h-0` is required so the flex child can
          shrink below its content size and actually engage
          `overflow-y-auto`. Without it, iPad Safari + Radix's
          fixed-position PopoverContent silently clip items past the
          viewport instead of scrolling. */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-4">
        {/* Groups section */}
        <section data-testid="bookmark-groups-section">
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.15em] mb-2 px-1"
            style={{ color: 'var(--theme-textSecondary)' }}
          >
            {t('generated.bookmarkorganizepopover_group_aca1')}
          </p>
          <div className="space-y-1">
            <GroupRow
              name="Ungrouped"
              icon={<Folder className="h-3.5 w-3.5" />}
              active={!currentGroupId}
              loading={busy === 'group:none'}
              onClick={() => moveToGroup(null)}
              testId="bookmark-group-ungrouped"
            />
            {groups.map((g) => (
              <GroupRow
                key={g.group_id}
                name={g.name}
                icon={<Folder className="h-3.5 w-3.5" />}
                active={currentGroupId === g.group_id}
                loading={busy === `group:${g.group_id}`}
                count={g.bookmark_count}
                onClick={() => moveToGroup(g.group_id)}
                testId={`bookmark-group-${g.group_id}`}
              />
            ))}
          </div>

          <form onSubmit={handleCreateGroup} className="mt-2 flex items-center gap-1.5">
            <div className="relative flex-1">
              <FolderPlus
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none"
                style={{ color: 'var(--theme-textSecondary)' }}
              />
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder={t('generated.bookmarkorganizepopover_new_group_ee7b')}
                maxLength={50}
                className="w-full text-xs rounded-full pl-8 pr-3 py-1.5 outline-none transition focus:ring-2"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--theme-text)',
                }}
                data-testid="bookmark-new-group-input"
              />
            </div>
            <button
              type="submit"
              disabled={creatingGroup || !newGroupName.trim()}
              className="h-7 w-7 rounded-full flex items-center justify-center transition disabled:opacity-50 active:scale-95"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              aria-label={t('generated.bookmarkorganizepopover_create_group_542b')}
              data-testid="bookmark-create-group-btn"
            >
              {creatingGroup ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            </button>
          </form>
        </section>

        {/* Labels section */}
        <section data-testid="bookmark-labels-section">
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.15em] mb-2 px-1"
            style={{ color: 'var(--theme-textSecondary)' }}
          >
            {t('generated.bookmarkorganizepopover_labels_49d8')}
            {currentLabelIds.size > 0 && (
              <span className="ml-2 normal-case tracking-normal" style={{ color: 'var(--theme-accent)' }}>
                {currentLabelIds.size} {t('generated.bookmarkorganizepopover_of_5_selected_83d9')}
              </span>
            )}
          </p>
          {labels.length === 0 ? (
            <p className="text-xs italic px-1 mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.bookmarkorganizepopover_no_labels_yet_845a')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {labels.map((lbl) => {
                const on = currentLabelIds.has(lbl.label_id);
                const isBusy = busy === `label:${lbl.label_id}`;
                return (
                  <button
                    key={lbl.label_id}
                    type="button"
                    onClick={() => toggleLabel(lbl.label_id)}
                    disabled={isBusy}
                    aria-pressed={on}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition active:scale-95 disabled:opacity-60"
                    style={{
                      background: on ? `color-mix(in srgb, ${lbl.color} 22%, transparent)` : 'var(--glass-bg)',
                      border: `1px solid ${on ? lbl.color : 'var(--glass-border)'}`,
                      color: on ? lbl.color : 'var(--theme-text)',
                    }}
                    data-testid={`bookmark-label-${lbl.label_id}`}
                  >
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ background: lbl.color }}
                    />
                    {lbl.name}
                    {isBusy && <Loader2 className="h-2.5 w-2.5 animate-spin ml-0.5" />}
                  </button>
                );
              })}
            </div>
          )}

          <form onSubmit={handleCreateLabel} className="mt-2 flex items-center gap-1.5">
            <div className="relative flex-1">
              <Tag
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none"
                style={{ color: 'var(--theme-textSecondary)' }}
              />
              <input
                type="text"
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                placeholder={t('generated.bookmarkorganizepopover_new_label_c258')}
                maxLength={30}
                className="w-full text-xs rounded-full pl-8 pr-3 py-1.5 outline-none transition focus:ring-2"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--theme-text)',
                }}
                data-testid="bookmark-new-label-input"
              />
            </div>
            <button
              type="submit"
              disabled={creatingLabel || !newLabelName.trim()}
              className="h-7 w-7 rounded-full flex items-center justify-center transition disabled:opacity-50 active:scale-95"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              aria-label={t('generated.bookmarkorganizepopover_create_label_10b1')}
              data-testid="bookmark-create-label-btn"
            >
              {creatingLabel ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            </button>
          </form>
        </section>
      </div>
      {/* Footer — remove */}
      <div
        className="px-3 py-2 border-t flex items-center justify-end shrink-0"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <button
          type="button"
          onClick={onRemove}
          disabled={!bookmarkId}
          className="text-[11px] font-medium px-2 py-1 rounded-md hover:bg-red-500/10 transition disabled:opacity-50"
          style={{ color: '#ef4444' }}
          data-testid="bookmark-remove-btn"
        >
          {t('generated.bookmarkorganizepopover_remove_bookmark_6075')}
        </button>
      </div>
    </div>
  );
}

function GroupRow({ name, icon, active, count, loading, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition active:scale-[0.99] disabled:opacity-60"
      style={{
        background: active ? 'color-mix(in srgb, var(--theme-accent) 14%, transparent)' : 'transparent',
        border: `1px solid ${active ? 'color-mix(in srgb, var(--theme-accent) 40%, transparent)' : 'transparent'}`,
      }}
      data-testid={testId}
      aria-pressed={active}
    >
      <span style={{ color: active ? 'var(--theme-accent)' : 'var(--theme-textSecondary)' }} className="shrink-0">
        {icon}
      </span>
      <span
        className="text-sm truncate flex-1"
        style={{ color: active ? 'var(--theme-text)' : 'var(--theme-text)', opacity: active ? 1 : 0.85 }}
      >
        {name}
      </span>
      {typeof count === 'number' && (
        <span className="text-[10px]" style={{ color: 'var(--theme-textSecondary)' }}>{count}</span>
      )}
      {active && !loading && <Check className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />}
      {loading && <Loader2 className="h-3 w-3 animate-spin shrink-0" style={{ color: 'var(--theme-accent)' }} />}
    </button>
  );
}
